import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { webColors, webBorderRadius } from '../../../constants/webStyles';
import { useIsMobile } from '../../../hooks/useIsMobile';

// Components
import AuthHeader from './components/AuthHeader.web';
import BrandingPanel from './components/BrandingPanel.web';
import { WebProgressSteps } from '../../../components/web';

// Steps
import StepCountry from './steps/StepCountry.web';
import StepFacility from './steps/StepFacility.web';
import StepDetails from './steps/StepDetails.web';
import StepLocation from './steps/StepLocation.web';
import StepVerify from './steps/StepVerify.web';
import StepSuccess from './steps/StepSuccess.web';

// Views
import LoginView from './views/LoginView.web';
import ForgotPasswordView from './views/ForgotPasswordView.web';

const STEPS = [
  { label: 'Country', component: StepCountry },
  { label: 'Facility', component: StepFacility },
  { label: 'Details', component: StepDetails },
  { label: 'Location', component: StepLocation },
  { label: 'Verify', component: StepVerify },
];

const FacilityAuth = () => {
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const [currentView, setCurrentView] = useState('register'); // 'register' | 'login' | 'forgotPassword'
  const [currentStep, setCurrentStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [formData, setFormData] = useState({
    // Country
    country: null,

    // Facility type
    facilityType: null,
    facilityTypeName: '',
    facilitySubtype: null,
    facilitySubtypeName: '',
    fac_id: null,

    // Facility details
    facilityName: '',
    department: '',
    businessRegistrationNumber: '',
    facilityPhone: '',
    profileImage: null,

    // Address
    facilityAddress: '',
    city: '',
    postalCode: '',
    state: '',

    // Account
    email: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,

    // OTP
    otp: '',

    // Location
    latitude: null,
    longitude: null,
    nonce: null,
  });

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= 5) {
      setCurrentStep(step);
    }
  };

  const handleComplete = () => {
    setIsComplete(true);
  };

  // View handlers
  const showLogin = () => setCurrentView('login');
  const showRegister = () => {
    setCurrentView('register');
    // Reset to step 1 when coming back to register
    if (isComplete) {
      setIsComplete(false);
      setCurrentStep(1);
      setFormData({
        country: null,
        facilityType: null,
        facilityTypeName: '',
        facilitySubtype: null,
        facilitySubtypeName: '',
        fac_id: null,
        facilityName: '',
        department: '',
        businessRegistrationNumber: '',
        facilityPhone: '',
        profileImage: null,
        facilityAddress: '',
        city: '',
        postalCode: '',
        state: '',
        email: '',
        password: '',
        confirmPassword: '',
        termsAccepted: false,
        otp: '',
        latitude: null,
        longitude: null,
        nonce: null,
      });
    }
  };
  const showForgotPassword = () => setCurrentView('forgotPassword');

  // Render success screen if registration complete
  if (currentView === 'register' && isComplete) {
    return <StepSuccess onShowLogin={showLogin} />;
  }

  const CurrentStepComponent = STEPS[currentStep - 1]?.component;
  const isDoctor = false; // This is the facility-only flow

  return (
    <View style={styles.container}>
      {/* Header */}
      <AuthHeader
        currentView={currentView}
        onShowLogin={showLogin}
        onShowRegister={showRegister}
      />

      {/* Progress Steps - Only for register view */}
      {currentView === 'register' && (
        <WebProgressSteps steps={STEPS} currentStep={currentStep} />
      )}

      {/* Main Content */}
      <View style={[styles.mainContainer, isMobile && styles.mainContainerMobile]}>
        {/* Left Side - Branding */}
        <BrandingPanel />

        {/* Right Side - Form */}
        <ScrollView style={styles.formSide} contentContainerStyle={[styles.formSideContent, isMobile && styles.formSideContentMobile]}>
          <View style={styles.formContainer}>
            {currentView === 'register' && CurrentStepComponent && (
              <CurrentStepComponent
                formData={formData}
                updateFormData={updateFormData}
                goToStep={goToStep}
                currentStep={currentStep}
                onComplete={handleComplete}
                isDoctor={isDoctor}
              />
            )}

            {currentView === 'login' && (
              <LoginView
                onShowForgotPassword={showForgotPassword}
                onShowRegister={showRegister}
                navigation={navigation}
              />
            )}

            {currentView === 'forgotPassword' && (
              <ForgotPasswordView
                onShowLogin={showLogin}
              />
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: webColors.background,
    minHeight: '100vh',
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 'calc(100vh - 140px)',
  },
  mainContainerMobile: {
    flexDirection: 'column',
    minHeight: 'auto',
  },
  formSide: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
  },
  formSideContent: {
    padding: 40,
    justifyContent: 'center',
    minHeight: '100%',
  },
  formSideContentMobile: {
    padding: 20,
  },
  formContainer: {
    maxWidth: 480,
    marginHorizontal: 'auto',
    width: '100%',
  },
});

export default FacilityAuth;
